import { defineStore } from 'pinia';
import { ref } from 'vue';
import salesAPI from '@/api/sales';
import type { Sale } from '@/types/Sale';
import { useAlertStore } from '@/store/alertStore';

export const useSalesStore = defineStore('sales', () => {
  const sales = ref<Sale[]>([]);
  const sold_by_user = ref<any>([]);
  const sold_by_medium = ref<any>([]);
  const sold_by_poster = ref<any>([]);
  const sold_by_product = ref<any>([]);
  const sales_totals = ref<any>([]);
  const sales_search = ref<any[]>([]);

  const alertStore = useAlertStore();
  
  const fetchSales = async (params: any): Promise<{ data: Sale[]; total: number }> => {
    try {
      const response = await salesAPI.getSales(params);
      const data: Sale[] = response.data.data;
      const total = response.data.meta.total;
      sales.value = data;
      return { data, total };
    } catch (error) {
      console.error('Error fetching sale s:', error);
      throw error;
    }
  };

  const fetchSaleById = async (id: number) => {
    try {
      const response = await salesAPI.getSaleById(id);
      return response.data.data as Sale;
    } catch (error) {
      console.error(`Error fetching sale  with ID ${id}:`, error);
      throw error;
    }
  };

  const createSale = async (saleData: Partial<Sale>) => {
    try {
      const response = await salesAPI.createSale(saleData);
      if (!response.data.success) {
        throw response.data;
      }
      sales.value.push(response.data.data as Sale);
      return response.data;
    } catch (error) {
      console.error('Error creating sale :', error);
      throw error;
    }
  };

  const updateSale = async (id: number, saleData: Partial<Sale>) => {
    try {
      const response = await salesAPI.updateSale(id, saleData);
      if (!response.data.success) {
        throw response.data;
      }
      const updated = response.data.data as Sale;
      const index = sales.value.findIndex((c:any) => c.id === id);
      if (index !== -1) {
        sales.value[index] = updated;
      }
      return response.data;
    } catch (error) {
      console.error(`Error updating sale  with ID ${id}:`, error);
      throw error;
    }
  };

  const deleteSale = async (id: number) => {
    try {
      await salesAPI.deleteSale(id);
      sales.value = sales.value.filter((c) => c.id !== id);
    } catch (error) {
      console.error(`Error deleting sale  with ID ${id}:`, error);
      throw error;
    }
  };

  
  const soldByUser = async (params: any): Promise<{ data: any; }> => {
    try{
      const response = await salesAPI.getSoldByUser(params);
      const data = response.data;
      sold_by_user.value = data
      return data;
    } catch (error){
      throw error;
    }
  };
  const soldByMedium = async (params: any): Promise<{ data: any; }> => {
    try{
      const response = await salesAPI.getSoldByMedium(params);
      const data = response.data;
      sold_by_medium.value = data
      return data;
    } catch (error){
      throw error;
    }
  };
  const soldByPoster = async (params: any): Promise<{ data: any; }> => {
    try{
      const response = await salesAPI.getSoldByPoster(params);
      const data = response.data;
      sold_by_poster.value = data
      return data;
    } catch (error){
      throw error;
    }
  };
  const soldByProduct = async (params: any): Promise<{ data: any; }> => {
    try{
      const response = await salesAPI.getSoldByProduct(params);
      const data = response.data;
      sold_by_product.value = data
      return data;
    } catch (error){
      throw error;
    }
  };
  const salesTotals = async (params: any): Promise<{ data: any; }> => {
    try{
      const response = await salesAPI.getSalesTotals(params);
      const data = response.data;
      sales_totals.value = data[0]
      return data;
    } catch (error){
      throw error;
    }
  };

  const authorizeSaleItem = async (saleItem: any) => {
    try{
      const response = await salesAPI.authorizeSaleItem(saleItem);
      if (!response.data.success) {
        throw response.data;
      }
      alertStore.showAlert('Detalle de venta autorizada con éxito', 'success');
      return response;
    } catch (error:any){
      alertStore.showAlert(
        error?.response?.data?.message || 'Ocurrió un error al autorizar el detalle de venta.',
        'error'
      );
      throw error;
    }
  };
  
  const authorizeSale = async (saleData: any) => {
    try {
      const response = await salesAPI.authorizeSale(saleData);
      if (!response.data.success) {
        throw response.data;
      }
      const updated = response.data.data as Sale;
      const index = sales.value.findIndex((c:any) => c.id === saleData.sale_id);
      if (index !== -1) {
        sales.value[index] = updated;
      }
      alertStore.showAlert('Venta autorizada con éxito', 'success');
      return response.data;
    } catch (error:any) {
      alertStore.showAlert(
        error?.response?.data?.message || 'Ocurrió un error al autorizar la venta.',
        'error'
      );
      throw error;
    }
  };

  const confirmSale = async (saleId: number) => {
    try {
      const response = await salesAPI.confirmSale(saleId);
      /*if (!response.data.success) {
        throw response.data;
      }
      const updated = response.data.data as Sale;
      const index = sales.value.findIndex((c:any) => c.id === saleId);
      if (index !== -1) {
        sales.value[index] = updated;
      }*/
      alertStore.showAlert('Venta confirmada con éxito', 'success');
      return response.data;
    } catch (error:any) {
      alertStore.showAlert(
        error?.response?.data?.message || 'Ocurrió un error al autorizar la venta.',
        'error'
      );
      throw error;
    }
  }

  const scheduledSale = async (id: number, saleData: Partial<Sale>) => {
    try {
      const response = await salesAPI.updateSale(id, { 
        additional_data: {
          ...(saleData?.additional_data ?? {}),
          scheduled: true,
        },
      });
      if (!response.data.success) {
        throw response.data;
      }
      const updated = response.data.data as Sale;
      const index = sales.value.findIndex((c:any) => c.id === id);
      if (index !== -1) {
        sales.value[index] = updated;
      }
      return response.data;
    } catch (error) {
      console.error(`Error updating sale  with ID ${id}:`, error);
      throw error;
    }
  };

  const exportCSV = async (params: string, body: any) => {
    try {
      return await salesAPI.exportCSV(params, body);
    } catch (error) {
      console.error('Error exportando archivo:', error);
      throw error;
    }
  };

  const exportInvoiceOrder = async (id: any) => {
  try {
    const response = await salesAPI.exportInvoiceOrder(id);

    // construimos el blob con el MIME correcto
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    // creamos un <a> temporal
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Orden de Facturación Venta ${id}.pdf`); // nombre dinámico
    document.body.appendChild(link);
    link.click();

    // limpieza
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('Error exportando archivo:', error);
    throw error;
  }
};

  const searchSales = async (params: any): Promise<{ data: Sale[]}> => {
    try {
      const response = await salesAPI.getSales(params);
      const data: Sale[] = response.data.data;
      sales_search.value = data;
      return { data };
    } catch (error) {
      console.error('Error fetching sale s:', error);
      throw error;
    }
  };

  return {
    sales,
    sales_search,
    fetchSales,
    fetchSaleById,
    createSale,
    updateSale,
    deleteSale,
    soldByUser,
    soldByMedium,
    soldByPoster,
    soldByProduct,
    salesTotals,
    sold_by_user,
    sold_by_medium,
    sold_by_poster,
    sold_by_product,
    sales_totals,
    searchSales,
    authorizeSaleItem,
    authorizeSale,
    exportCSV,
    confirmSale,
    scheduledSale,
    exportInvoiceOrder
  };
});
